<template>
  <section class="dashboard-page" :dir="pageDirection">
    <!-- Loading -->
    <div v-if="loading" class="loading-wrapper">
      <div class="loading-card">
        <v-progress-circular
          indeterminate
          size="70"
          width="6"
          color="primary"
        />

        <h3>{{ t("dashboardPage.loadingTitle") }}</h3>
        <p>{{ t("dashboardPage.loadingDescription") }}</p>
      </div>
    </div>

    <template v-else>
      <!-- Error -->
      <v-alert
        v-if="errorMessage"
        type="error"
        variant="tonal"
        closable
        class="mb-4"
        @click:close="errorMessage = ''"
      >
        {{ errorMessage }}
      </v-alert>

      <!-- Header -->
      <div class="section-head">
        <h2>{{ t("dashboardPage.title") }}</h2>

        <p>{{ t("dashboardPage.description") }}</p>

        <span>{{ t("dashboardPage.source") }}</span>
      </div>

      <!-- Filters -->
      <div class="filter-panel">
        <div class="filter-title">
          <v-icon size="20"> mdi-filter-variant </v-icon>

          {{ t("dashboardPage.filters.title") }}
        </div>

        <div class="filter-grid">
          <v-select
            v-model="filters.governorate"
            :label="t('dashboardPage.filters.governorate')"
            :items="governorates"
            clearable
            variant="outlined"
            density="compact"
          />

          <v-select
            v-model="filters.district"
            :label="t('dashboardPage.filters.district')"
            :items="filteredDistricts"
            clearable
            variant="outlined"
            density="compact"
          />

          <v-select
            v-model="filters.facilityType"
            :label="t('dashboardPage.filters.facilityType')"
            :items="facilityTypes"
            clearable
            variant="outlined"
            density="compact"
          />

          <v-select
            v-model="filters.priority"
            :label="t('dashboardPage.filters.priority')"
            :items="priorityLevels"
            clearable
            variant="outlined"
            density="compact"
          />

          <v-select
            v-model="filters.operationalStatus"
            :label="t('dashboardPage.filters.operationalStatus')"
            :items="operationalStatuses"
            clearable
            variant="outlined"
            density="compact"
          />

          <v-btn
            height="40"
            color="primary"
            prepend-icon="mdi-filter"
            :loading="loading"
            @click="loadData"
          >
            {{ t("dashboardPage.filters.apply") }}
          </v-btn>

          <v-btn
            height="40"
            class="reset-btn"
            prepend-icon="mdi-refresh"
            :loading="loading"
            @click="resetFilters"
          >
            {{ t("dashboardPage.filters.reset") }}
          </v-btn>
        </div>
      </div>

      <!-- Priority summary -->
      <div class="priority-panel">
        <div class="priority-panel__icon">
          <v-icon>mdi-clipboard-list-outline</v-icon>
        </div>

        <div class="priority-panel__content">
          <div class="priority-panel__heading">
            <div>
              <h3>{{ t("dashboardPage.priority.title") }}</h3>
              <p>{{ t("dashboardPage.priority.description") }}</p>
            </div>

            <span class="priority-total">
              {{ t("dashboardPage.priority.total") }}
              <b>{{ filteredFacilities.length }}</b>
            </span>
          </div>

          <div class="priority-summary">
            <div class="priority-item priority-item--critical">
              <span class="priority-item__indicator"></span>

              <div class="priority-item__text">
                <strong>{{ t("dashboardPage.priority.p1") }}</strong>
                <small>P1</small>
              </div>

              <b class="priority-item__count">{{ priorityCounts.P1 }}</b>
            </div>

            <div class="priority-item priority-item--high">
              <span class="priority-item__indicator"></span>

              <div class="priority-item__text">
                <strong>{{ t("dashboardPage.priority.p2") }}</strong>
                <small>P2</small>
              </div>

              <b class="priority-item__count">{{ priorityCounts.P2 }}</b>
            </div>

            <div class="priority-item priority-item--medium">
              <span class="priority-item__indicator"></span>

              <div class="priority-item__text">
                <strong>{{ t("dashboardPage.priority.p3") }}</strong>
                <small>P3</small>
              </div>

              <b class="priority-item__count">{{ priorityCounts.P3 }}</b>
            </div>

            <div class="priority-item priority-item--low">
              <span class="priority-item__indicator"></span>

              <div class="priority-item__text">
                <strong>{{ t("dashboardPage.priority.p4") }}</strong>
                <small>P4</small>
              </div>

              <b class="priority-item__count">{{ priorityCounts.P4 }}</b>
            </div>
          </div>
        </div>

        <router-link to="/priorities" class="priority-action">
          <span>{{ t("dashboardPage.priority.view") }}</span>
          <v-icon size="19">mdi-arrow-left</v-icon>
        </router-link>
      </div>

      <!-- KPI cards -->
      <div class="kpi-grid">
        <article
          v-for="item in dashboardKpis"
          :key="item.key"
          class="kpi-card"
          :class="item.color"
        >
          <div class="kpi-title">
            {{ item.title }}
          </div>

          <div class="kpi-value">
            {{ item.value }}
          </div>

          <div class="kpi-note">
            {{ item.note }}
          </div>
        </article>
      </div>

      <!-- Axis summary -->
      <div class="axes-panel">
        <div class="axes-head">
          <div>
            <h3>{{ t("dashboardPage.axes.title") }}</h3>

            <p>{{ t("dashboardPage.axes.description") }}</p>
          </div>

          <span>{{ t("dashboardPage.axes.count") }}</span>
        </div>

        <div class="summary-grid">
          <router-link
            v-for="axis in axes"
            :key="axis.code"
            class="summary-card axis-link"
            :to="axis.route"
            :style="{ borderTopColor: axis.color }"
          >
            <div class="summary-top">
              <span class="dot" :style="{ background: axis.color }" />

              <strong>
                {{ axis.short }}
              </strong>
            </div>

            <div class="summary-value">{{ formatNumber(axis.score, 1) }}%</div>

            <p>
              {{ t("dashboardPage.axes.weight", { weight: axis.weightLabel }) }}
            </p>
          </router-link>
        </div>
      </div>

      <!-- Charts -->
      <div class="charts-grid professional-charts">
        <!-- Financial coverage -->
        <div class="chart-card">
          <h3>{{ t("dashboardPage.charts.financialCoverage") }}</h3>

          <div v-if="!financialRows.length" class="empty-chart">
            {{ t("dashboardPage.charts.noFinancialData") }}
          </div>

          <apexchart
            v-else
            type="bar"
            height="420"
            :series="financialCoverageSeries"
            :options="financialCoverageOptions"
          />
        </div>

        <!-- FIS -->
        <div class="chart-card">
          <h3>{{ t("dashboardPage.charts.fisRanking") }}</h3>

          <div v-if="!fisRows.length" class="empty-chart">
            {{ t("dashboardPage.charts.noFisData") }}
          </div>

          <apexchart
            v-else
            type="bar"
            height="420"
            :series="fisStackedSeries"
            :options="fisStackedOptions"
          />
        </div>

        <!-- SPCI -->
        <div class="chart-card">
          <h3>
            {{ t("dashboardPage.charts.servicePackage") }}

            <span class="code-badge">SPCI</span>

            {{ t("dashboardPage.charts.standard80") }}
          </h3>

          <div v-if="!spciRows.length" class="empty-chart">
            {{ t("dashboardPage.charts.noServiceData") }}
          </div>

          <apexchart
            v-else
            type="bar"
            height="420"
            :series="spciSeries"
            :options="spciOptions"
          />
        </div>

        <!-- Electricity -->
        <div class="chart-card">
          <h3>{{ t("dashboardPage.charts.electricity") }}</h3>

          <div v-if="!electricityRows.length" class="empty-chart">
            {{ t("dashboardPage.charts.noElectricityData") }}
          </div>

          <apexchart
            v-else
            type="bar"
            height="420"
            :series="electricitySeries"
            :options="electricityOptions"
          />
        </div>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import api from "@/plugins/api";

const { t, locale } = useI18n();

const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));

/*
|--------------------------------------------------------------------------
| State
|--------------------------------------------------------------------------
*/

const loading = ref(true);
const errorMessage = ref("");

const cards = ref({});
const facilities = ref([]);

const dashboardCharts = ref({
  financial_coverage: [],
  integrated_result: [],
  service_package: [],
  electricity_availability: [],
});

const filters = reactive({
  governorate: null,
  district: null,
  facilityType: null,
  priority: null,
  operationalStatus: null,
});

const priorityLevels = ["P1", "P2", "P3", "P4"];

/*
|--------------------------------------------------------------------------
| Helpers
|--------------------------------------------------------------------------
*/

const normalize = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
};

const numberValue = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const normalized =
    typeof value === "string" ? value.replaceAll(",", "").trim() : value;

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatNumber = (value, digits = 0) => {
  return numberValue(value).toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const firstValue = (object, keys, fallback = 0) => {
  for (const key of keys) {
    const value = object?.[key];

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return fallback;
};

const facilityName = (item) => {
  const keys =
    locale.value === "ar"
      ? [
          "facility_name_ar",
          "facility_name_en",
          "facility_name",
          "name",
          "hf_name",
        ]
      : [
          "facility_name_en",
          "facility_name_ar",
          "facility_name",
          "name",
          "hf_name",
        ];

  return firstValue(item, keys, t("dashboardPage.common.unspecified"));
};

const normalizePriority = (value) => {
  const text = normalize(value);

  if (text === "P1" || text.includes("الأولى") || text.includes("حرجة")) {
    return "P1";
  }

  if (text === "P2" || text.includes("الثانية") || text.includes("عالية")) {
    return "P2";
  }

  if (text === "P3" || text.includes("الثالثة") || text.includes("متوسطة")) {
    return "P3";
  }

  if (text === "P4" || text.includes("الرابعة") || text.includes("منخفضة")) {
    return "P4";
  }

  return text || "P4";
};

const normalizeFacility = (item, index) => {
  return {
    id: firstValue(item, ["facility_id", "survey_id", "id"], index + 1),

    surveyId: firstValue(item, ["survey_id", "id"], index + 1),

    name: facilityName(item),

    governorate: firstValue(
      item,
      locale.value === "ar"
        ? [
            "governorate_name_ar",
            "governorate_name_en",
            "governorate",
            "province_name_ar",
          ]
        : [
            "governorate_name_en",
            "governorate_name_ar",
            "governorate",
            "province_name_en",
          ],
      "-"
    ),

    district: firstValue(
      item,
      locale.value === "ar"
        ? ["district_name_ar", "district_name_en", "district"]
        : ["district_name_en", "district_name_ar", "district"],
      "-"
    ),

    facilityType: firstValue(
      item,
      locale.value === "ar"
        ? [
            "facility_type_ar",
            "facility_type_en",
            "facility_type",
            "type_name_ar",
          ]
        : [
            "facility_type_en",
            "facility_type_ar",
            "facility_type",
            "type_name_en",
          ],
      "-"
    ),

    operationalStatus: firstValue(
      item,
      locale.value === "ar"
        ? [
            "operational_status_ar",
            "operational_status_en",
            "operational_status",
          ]
        : [
            "operational_status_en",
            "operational_status_ar",
            "operational_status",
          ],
      "-"
    ),

    priority: normalizePriority(
      firstValue(item, ["priority_code", "priority", "priority_category"], "P4")
    ),

    fis: numberValue(firstValue(item, ["FIS", "fis", "priority_score"], 0)),

    totalStaff: numberValue(
      firstValue(item, ["total_staff", "cadre_total_count"], 0)
    ),

    beneficiariesMonth: numberValue(
      firstValue(
        item,
        [
          "monthly_beneficiaries",
          "beneficiaries_current",
          "beneficiaries_month",
        ],
        0
      )
    ),

    targetPopulation: numberValue(firstValue(item, ["target_population"], 0)),
  };
};

/*
|--------------------------------------------------------------------------
| Request parameters
|--------------------------------------------------------------------------
*/

const buildRequestParams = () => {
  const params = {};

  if (filters.governorate) {
    params.governorate = filters.governorate;
  }

  if (filters.district) {
    params.district = filters.district;
  }

  if (filters.facilityType) {
    params.facility_type = filters.facilityType;
  }

  if (filters.priority) {
    params.priority = filters.priority;
  }

  if (filters.operationalStatus) {
    params.operational_status = filters.operationalStatus;
  }

  return params;
};

/*
|--------------------------------------------------------------------------
| Load API
|--------------------------------------------------------------------------
*/

const loadData = async () => {
  loading.value = true;
  errorMessage.value = "";

  try {
    const response = await api.get("/reports/national-dashboard-cards", {
      params: buildRequestParams(),
    });

    const payload = response?.data ?? {};

    cards.value = payload.cards ?? {};

    facilities.value = Array.isArray(payload.facilities)
      ? payload.facilities.map(normalizeFacility)
      : [];

    dashboardCharts.value = {
      financial_coverage: Array.isArray(payload?.charts?.financial_coverage)
        ? payload.charts.financial_coverage
        : [],

      integrated_result: Array.isArray(payload?.charts?.integrated_result)
        ? payload.charts.integrated_result
        : [],

      service_package: Array.isArray(payload?.charts?.service_package)
        ? payload.charts.service_package
        : [],

      electricity_availability: Array.isArray(
        payload?.charts?.electricity_availability
      )
        ? payload.charts.electricity_availability
        : [],
    };

    console.log(
      "Financial coverage rows:",
      dashboardCharts.value.financial_coverage
    );
  } catch (error) {
    console.error("Dashboard API Error:", error);

    errorMessage.value =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      t("dashboardPage.loadError");
  } finally {
    loading.value = false;
  }
};

onMounted(loadData);

/*
|--------------------------------------------------------------------------
| Filter options
|--------------------------------------------------------------------------
*/

const governorates = computed(() => {
  return [
    ...new Set(
      facilities.value
        .map((item) => item.governorate)
        .filter((value) => value && value !== "-")
    ),
  ];
});

const filteredDistricts = computed(() => {
  let data = facilities.value;

  if (filters.governorate) {
    data = data.filter((item) => item.governorate === filters.governorate);
  }

  return [
    ...new Set(
      data
        .map((item) => item.district)
        .filter((value) => value && value !== "-")
    ),
  ];
});

const facilityTypes = computed(() => {
  return [
    ...new Set(
      facilities.value
        .map((item) => item.facilityType)
        .filter((value) => value && value !== "-")
    ),
  ];
});

const operationalStatuses = computed(() => {
  return [
    ...new Set(
      facilities.value
        .map((item) => item.operationalStatus)
        .filter((value) => value && value !== "-")
    ),
  ];
});

const filteredFacilities = computed(() => {
  return facilities.value.filter((item) => {
    return (
      (!filters.governorate || item.governorate === filters.governorate) &&
      (!filters.district || item.district === filters.district) &&
      (!filters.facilityType || item.facilityType === filters.facilityType) &&
      (!filters.priority || item.priority === filters.priority) &&
      (!filters.operationalStatus ||
        item.operationalStatus === filters.operationalStatus)
    );
  });
});

// const criticalFacilities = computed(() => {
//   return filteredFacilities.value.filter((item) => item.priority === "P1")
//     .length;
// });

/*
|--------------------------------------------------------------------------
| Integrated rows
|--------------------------------------------------------------------------
*/

const integratedRows = computed(() => {
  return dashboardCharts.value.integrated_result.map((item) => {
    return {
      ...item,

      name: facilityName(item),

      priority: normalizePriority(item.priority_code),

      infrastructure: numberValue(
        firstValue(item, ["infrastructure", "infra_100"], 0)
      ),

      services: numberValue(firstValue(item, ["services", "services_100"], 0)),

      staff: numberValue(firstValue(item, ["staff", "staff_100"], 0)),

      finance: numberValue(firstValue(item, ["finance", "fin_100"], 0)),

      governance: numberValue(
        firstValue(item, ["governance", "gov_avg_100"], 0)
      ),
    };
  });
});

/*
|--------------------------------------------------------------------------
| FIS calculation
|--------------------------------------------------------------------------
*/
const priorityCounts = computed(() => ({
  P1: filteredFacilities.value.filter((f) => f.priority === "P1").length,
  P2: filteredFacilities.value.filter((f) => f.priority === "P2").length,
  P3: filteredFacilities.value.filter((f) => f.priority === "P3").length,
  P4: filteredFacilities.value.filter((f) => f.priority === "P4").length,
}));

/*
|--------------------------------------------------------------------------
| KPI cards
|--------------------------------------------------------------------------
*/

const dashboardKpis = computed(() => {
  const data = cards.value ?? {};

  const total = numberValue(data.total_facilities);

  const fundingGapYer = numberValue(
    data.monthly_funding_gap_yer ?? data.funding_gap_yer
  );

  const currency =
    data.currency_symbol ||
    data.currency_name ||
    t("dashboardPage.currency.yer");

  return [
    {
      key: "assessed",

      title: t("dashboardPage.kpis.assessed.title"),

      value: formatNumber(data.assessed_facilities),

      note: t("dashboardPage.kpis.assessed.note", {
        value: formatNumber(data.assessment_coverage, 0),
      }),

      color: "blue",
    },

    {
      key: "partial",

      title: t("dashboardPage.kpis.partial.title"),

      value: `${formatNumber(data.partially_operational_facilities)} ${t(
        "dashboardPage.common.of"
      )} ${formatNumber(total)}`,

      note: `${formatNumber(data.fully_operational_facilities)} ${t(
        "dashboardPage.kpis.partial.fullyOperational"
      )}`,

      color: "orange",
    },

    {
      key: "structural",

      title: t("dashboardPage.kpis.structural.title"),

      value: `${formatNumber(
        data.average_structural_condition,
        2
      )} / ${formatNumber(data.structural_condition_max || 5)}`,

      note: t("dashboardPage.kpis.structural.note", {
        value: formatNumber(data.average_fsci_pct, 1),
      }),

      color: "orange",
    },

    {
      key: "services",

      title: t("dashboardPage.kpis.services.title"),

      value: `${formatNumber(data.average_service_package, 1)}%`,

      note: t("dashboardPage.common.standardValue", {
        value: formatNumber(data.service_package_standard || 80),
      }),

      color: "red",
    },

    {
      key: "medicines",

      title: t("dashboardPage.kpis.medicines.title"),

      value: `${formatNumber(data.average_medicines_availability, 1)}%`,

      note: t("dashboardPage.common.standardValue", {
        value: formatNumber(data.medicines_standard || 70),
      }),

      color: "red",
    },

    {
      key: "funding",

      title: t("dashboardPage.kpis.funding.title"),

      value: `${formatNumber(fundingGapYer / 1550)} ${t(
        "dashboardPage.currency.usd"
      )}`,

      note:
        fundingGapYer > 0
          ? `≈ ${formatNumber(fundingGapYer)} ${currency}`
          : t("dashboardPage.kpis.funding.unavailable"),

      color: "red",
    },

    {
      key: "emergency",

      title: t("dashboardPage.kpis.emergency.title"),

      value: `${formatNumber(data.emergency_ready_facilities ?? 0)} ${t(
        "dashboardPage.common.of"
      )} ${formatNumber(total)}`,

      note: t("dashboardPage.kpis.emergency.note"),

      color: "red",
    },

    {
      key: "disability",

      title: t("dashboardPage.kpis.disability.title"),

      value: `${formatNumber(data.average_disability_access ?? 0, 1)} / 100`,

      note: `المعيار: ${formatNumber(
        data.disability_access_standard ?? 60
      )} — ${t("dashboardPage.kpis.disability.limited")}`,

      color: "orange",
    },

    {
      key: "staff",

      title: t("dashboardPage.kpis.staff.title"),

      value: formatNumber(data.total_staff),

      note: `${t("dashboardPage.kpis.staff.femaleAverage")} ${formatNumber(
        data.average_female_staff_ratio,
        1
      )}%`,

      color: "blue",
    },

    {
      key: "beneficiaries",

      title: t("dashboardPage.kpis.beneficiaries.title"),

      value: formatNumber(data.monthly_beneficiaries),

      note: t("dashboardPage.kpis.beneficiaries.note", {
        value: formatNumber(data.target_population),
      }),

      color: "blue",
    },
  ];
});

/*
|--------------------------------------------------------------------------
| Axis summary
|--------------------------------------------------------------------------
*/

const average = (values) => {
  const valid = values
    .map((value) => numberValue(value, Number.NaN))
    .filter(Number.isFinite);

  if (!valid.length) {
    return 0;
  }

  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
};

const axes = computed(() => {
  const rows = integratedRows.value;

  return [
    {
      code: "INF",
      short: t("dashboardPage.axes.infrastructure"),
      weightLabel: "25%",
      color: "#0ea5e9",
      route: "/assessment/infrastructure/structural",
      score: average(rows.map((item) => item.infrastructure)),
    },

    {
      code: "SRV",
      short: t("dashboardPage.axes.services"),
      weightLabel: "30%",
      color: "#14b8a6",
      route: "/assessment/services/availability",
      score: average(rows.map((item) => item.services)),
    },

    {
      code: "HRH",
      short: t("dashboardPage.axes.workforce"),
      weightLabel: "20%",
      color: "#8b5cf6",
      route: "/assessment/staff/summary",
      score: average(rows.map((item) => item.staff)),
    },

    {
      code: "FIN",
      short: t("dashboardPage.axes.finance"),
      weightLabel: "15%",
      color: "#10b981",
      route: "/assessment/finance/status",
      score: average(rows.map((item) => item.finance)),
    },

    {
      code: "GOV",
      short: t("dashboardPage.axes.governance"),
      weightLabel: "10%",
      color: "#f59e0b",
      route: "/assessment/governance/structure",
      score: average(rows.map((item) => item.governance)),
    },
  ];
});

/*
|--------------------------------------------------------------------------
| Reset filters
|--------------------------------------------------------------------------
*/

const resetFilters = async () => {
  filters.governorate = null;
  filters.district = null;
  filters.facilityType = null;
  filters.priority = null;
  filters.operationalStatus = null;

  await loadData();
};

watch(
  () => filters.governorate,
  () => {
    filters.district = null;
  }
);

/*
|--------------------------------------------------------------------------
| Chart helpers
|--------------------------------------------------------------------------
*/

const barColor = (value, standard) => {
  if (value >= standard) {
    return "#1f7a43";
  }

  if (value >= standard * 0.75) {
    return "#b45309";
  }

  return "#c51f24";
};

const makeRows = (items, keys) => {
  return (items || [])
    .map((item) => {
      return {
        name: facilityName(item),

        value: numberValue(firstValue(item, keys, 0)),

        raw: item,
      };
    })
    .filter((item) => item.name !== "غير محدد")
    .sort((first, second) => second.value - first.value)
    .slice(0, 9);
};

const baseBarOptions = (
  rows,
  standard,
  label = t("dashboardPage.common.standard")
) => {
  return {
    chart: {
      toolbar: {
        show: false,
      },

      fontFamily: "Cairo",

      animations: {
        enabled: true,
      },
    },

    plotOptions: {
      bar: {
        columnWidth: "62%",
        borderRadius: 2,
        distributed: true,
      },
    },

    colors: rows.map((item) => {
      return barColor(item.value, standard);
    }),

    dataLabels: {
      enabled: true,

      formatter: (value) => {
        return `${numberValue(value).toFixed(1)}%`;
      },

      offsetY: -6,

      style: {
        fontSize: "11px",
        fontFamily: "Cairo",
        fontWeight: 800,
      },

      background: {
        enabled: false,
      },
    },

    stroke: {
      width: 0,
    },

    grid: {
      borderColor: "#e2e8f0",
    },

    xaxis: {
      categories: rows.map((item) => item.name),

      labels: {
        rotate: -35,
        trim: false,
        hideOverlappingLabels: false,

        style: {
          colors: "#526174",
          fontSize: "12px",
          fontFamily: "Cairo",
        },
      },
    },

    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 5,

      labels: {
        formatter: (value) => {
          return numberValue(value).toFixed(0);
        },

        style: {
          colors: "#526174",
          fontFamily: "Cairo",
        },
      },
    },

    annotations: {
      yaxis: [
        {
          y: standard,

          borderColor: "#173b63",

          strokeDashArray: 6,

          label: {
            text: `${label} ${standard}%`,

            position: "left",

            style: {
              color: "#173b63",
              background: "#ffffff",
              fontSize: "12px",
              fontFamily: "Cairo",
            },
          },
        },
      ],
    },

    tooltip: {
      y: {
        formatter: (value) => {
          return `${numberValue(value).toFixed(1)}%`;
        },
      },
    },

    legend: {
      show: false,
    },

    noData: {
      text: t("dashboardPage.common.noData"),
    },
  };
};

/*
|--------------------------------------------------------------------------
| Financial coverage
|--------------------------------------------------------------------------
|
| الاستجابة تستخدم score لذلك يجب أن يكون أول حقل.
|--------------------------------------------------------------------------
*/

const financialRows = computed(() => {
  return makeRows(dashboardCharts.value.financial_coverage, [
    "score",
    "FIN_01",
    "fin_01",
    "coverage_percentage",
    "financial_coverage",
    "coverage_rate",
    "avg_coverage",
  ]);
});

const financialCoverageSeries = computed(() => {
  return [
    {
      name: t("dashboardPage.series.financialCoverage"),

      data: financialRows.value.map((item) => Number(item.value.toFixed(1))),
    },
  ];
});

const financialCoverageOptions = computed(() => {
  const options = baseBarOptions(financialRows.value, 80);

  options.tooltip = {
    custom: ({ dataPointIndex }) => {
      const row = financialRows.value[dataPointIndex];

      if (!row) {
        return "";
      }

      const available = numberValue(row.raw?.available_yer);

      const required = numberValue(row.raw?.required_yer);

      return `
          <div
            dir="${pageDirection.value}"
            style="
              padding:12px;
              text-align:${locale.value === "ar" ? "right" : "left"};
              font-family:Cairo;
              min-width:220px;
            "
          >
            <strong>
              ${row.name}
            </strong>

            <div style="margin-top:8px">
              ${t("dashboardPage.tooltip.coverage")}:
              <b>
                ${row.value.toFixed(1)}%
              </b>
            </div>

            <div style="margin-top:5px">
              ${t("dashboardPage.tooltip.available")}:
              ${available.toLocaleString("en-US")}
              ${t("dashboardPage.currency.yer")}
            </div>

            <div style="margin-top:5px">
              ${t("dashboardPage.tooltip.required")}:
              ${required.toLocaleString("en-US")}
              ${t("dashboardPage.currency.yer")}
            </div>
          </div>
        `;
    },
  };

  return options;
});

/*
|--------------------------------------------------------------------------
| Service package
|--------------------------------------------------------------------------
*/

const spciRows = computed(() => {
  return makeRows(dashboardCharts.value.service_package, [
    "spci_pct",
    "SPCI_pct",
    "SPCI",
    "service_package_percentage",
    "service_package_pct",
  ]);
});

const spciSeries = computed(() => {
  return [
    {
      name: "SPCI",

      data: spciRows.value.map((item) => Number(item.value.toFixed(1))),
    },
  ];
});

const spciOptions = computed(() => {
  return baseBarOptions(spciRows.value, 80);
});

/*
|--------------------------------------------------------------------------
| Electricity
|--------------------------------------------------------------------------
*/

const electricityRows = computed(() => {
  return makeRows(dashboardCharts.value.electricity_availability, [
    "score",
    "INF_07",
    "inf_07",
    "electricity_percentage",
    "electricity_pct",
    "availability_pct",
  ]);
});

const electricitySeries = computed(() => {
  return [
    {
      name: t("dashboardPage.series.electricity"),

      data: electricityRows.value.map((item) => Number(item.value.toFixed(1))),
    },
  ];
});

const electricityOptions = computed(() => {
  return baseBarOptions(electricityRows.value, 75);
});

/*
|--------------------------------------------------------------------------
| Weighted FIS rows
|--------------------------------------------------------------------------
*/

const fisRows = computed(() => {
  return [...integratedRows.value]
    .map((item) => {
      const infrastructureWeighted = numberValue(item.infrastructure) * 0.25;

      const servicesWeighted = numberValue(item.services) * 0.3;

      const staffWeighted = numberValue(item.staff) * 0.2;

      const financeWeighted = numberValue(item.finance) * 0.15;

      const governanceWeighted = numberValue(item.governance) * 0.1;

      return {
        ...item,

        infrastructureWeighted,
        servicesWeighted,
        staffWeighted,
        financeWeighted,
        governanceWeighted,

        fis:
          infrastructureWeighted +
          servicesWeighted +
          staffWeighted +
          financeWeighted +
          governanceWeighted,
      };
    })
    .sort((first, second) => second.fis - first.fis)
    .slice(0, 9);
});

const fisStackedSeries = computed(() => {
  return [
    {
      name: t("dashboardPage.series.infrastructure25"),

      data: fisRows.value.map((item) =>
        Number(item.infrastructureWeighted.toFixed(1))
      ),
    },

    {
      name: t("dashboardPage.series.services30"),

      data: fisRows.value.map((item) =>
        Number(item.servicesWeighted.toFixed(1))
      ),
    },

    {
      name: t("dashboardPage.series.workforce20"),

      data: fisRows.value.map((item) => Number(item.staffWeighted.toFixed(1))),
    },

    {
      name: t("dashboardPage.series.finance15"),

      data: fisRows.value.map((item) =>
        Number(item.financeWeighted.toFixed(1))
      ),
    },

    {
      name: t("dashboardPage.series.governance10"),

      data: fisRows.value.map((item) =>
        Number(item.governanceWeighted.toFixed(1))
      ),
    },
  ];
});

const fisStackedOptions = computed(() => {
  return {
    chart: {
      stacked: true,

      toolbar: {
        show: false,
      },

      fontFamily: "Cairo",
    },

    colors: ["#173b63", "#147c91", "#7c3aed", "#10b981", "#b8923e"],

    plotOptions: {
      bar: {
        columnWidth: "62%",
        borderRadius: 0,
      },
    },

    dataLabels: {
      enabled: false,
    },

    grid: {
      borderColor: "#e2e8f0",
    },

    xaxis: {
      categories: fisRows.value.map((item) => item.name),

      labels: {
        rotate: -35,
        trim: false,

        hideOverlappingLabels: false,

        style: {
          colors: "#526174",
          fontSize: "12px",
          fontFamily: "Cairo",
        },
      },
    },

    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 5,

      labels: {
        formatter: (value) => {
          return numberValue(value).toFixed(0);
        },

        style: {
          colors: "#526174",
          fontFamily: "Cairo",
        },
      },
    },

    legend: {
      position: "top",

      horizontalAlign: "center",

      fontFamily: "Cairo",

      markers: {
        width: 13,
        height: 13,
        radius: 0,
      },
    },

    tooltip: {
      shared: true,
      intersect: false,

      y: {
        formatter: (value) => {
          return `${numberValue(value).toFixed(1)} ${t(
            "dashboardPage.common.points"
          )}`;
        },
      },
    },

    noData: {
      text: t("dashboardPage.charts.noFisData"),
    },
  };
});
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;500;600;700;800;900&display=swap");

.dashboard-page {
  min-height: 100vh;
  padding: 26px 20px 60px;
  color: #1a2433;
  background: #f4f6f9;
  font-family: "Cairo", Tahoma, sans-serif;
}

.loading-wrapper {
  position: fixed;
  z-index: 99999;
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(255, 255, 255, 0.93);

  backdrop-filter: blur(4px);
}

.loading-card {
  width: 320px;
  max-width: 90%;
  padding: 34px 24px;

  text-align: center;

  background: #ffffff;

  border: 1px solid #dde4ec;

  border-radius: 20px;

  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12);
}

.loading-card h3 {
  margin-top: 18px;
  color: #0f172a;
  font-size: 18px;
  font-weight: 800;
}

.loading-card p {
  margin-top: 8px;
  color: #64748b;
  font-size: 13px;
}

.section-head {
  margin-bottom: 22px;
  text-align: center;
}

.section-head h2 {
  margin-bottom: 8px;
  color: #0f172a;
  font-size: 22px;
  font-weight: 900;
}

.section-head p {
  margin-bottom: 10px;
  color: #4c5b6e;
  font-size: 13px;
}

.section-head span {
  display: inline-block;
  padding: 4px 14px;

  color: #7a8799;
  font-size: 12px;

  background: #f8fafc;

  border: 1px solid #dde4ec;

  border-radius: 999px;
}

.filter-panel,
.axes-panel {
  padding: 16px;
  margin-bottom: 18px;

  background: #ffffff;

  border: 1px solid #dde4ec;

  border-radius: 12px;

  box-shadow: 0 1px 3px rgba(13, 34, 64, 0.08);
}

.filter-title {
  display: flex;
  align-items: center;
  gap: 8px;

  margin-bottom: 14px;

  color: #1a2433;
  font-size: 15px;
  font-weight: 800;
}

.filter-grid {
  display: grid;

  grid-template-columns: repeat(7, minmax(0, 1fr));

  gap: 12px;
  align-items: start;
}

.reset-btn {
  color: #2a1f08 !important;
  background: #b8923e !important;

  border-radius: 9px !important;

  font-weight: 800;
}

.priority-panel {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 18px;
  padding: 18px;
  margin-bottom: 20px;
  background: #ffffff;
  border: 1px solid #dbe3ee;
  border-radius: 16px;
  box-shadow: 0 6px 22px rgba(15, 23, 42, 0.06);
}

.priority-panel__icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #173b63;
  background: #edf4fb;
  border: 1px solid #d5e3f1;
  border-radius: 14px;
  font-size: 25px;
}

.priority-panel__content {
  min-width: 0;
}

.priority-panel__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
}

.priority-panel__heading h3 {
  margin: 0 0 3px;
  color: #0f172a;
  font-size: 16px;
  font-weight: 900;
}

.priority-panel__heading p {
  margin: 0;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.priority-total {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px;
  color: #526174;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
}

.priority-total b {
  min-width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #173b63;
  background: #ffffff;
  border: 1px solid #dbe3ee;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 900;
}

.priority-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(150px, 1fr));
  gap: 10px;
}

.priority-item {
  position: relative;
  min-width: 0;
  min-height: 68px;
  display: grid;
  grid-template-columns: 7px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: transform 0.18s ease, box-shadow 0.18s ease,
    border-color 0.18s ease;
}

.priority-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.priority-item__indicator {
  width: 6px;
  height: 38px;
  border-radius: 999px;
}

.priority-item__text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.priority-item__text strong {
  overflow: hidden;
  color: #1e293b;
  font-size: 12px;
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.priority-item__text small {
  color: #64748b;
  font-size: 10px;
  font-weight: 800;
  direction: ltr;
}

.priority-item__count {
  min-width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 900;
}

.priority-item--critical {
  background: #fff7f7;
  border-color: #fecaca;
}
.priority-item--critical .priority-item__indicator {
  background: #dc2626;
}
.priority-item--critical .priority-item__count {
  color: #b91c1c;
  border: 1px solid #fecaca;
}
.priority-item--high {
  background: #fff9f5;
  border-color: #fed7aa;
}
.priority-item--high .priority-item__indicator {
  background: #ea580c;
}
.priority-item--high .priority-item__count {
  color: #c2410c;
  border: 1px solid #fed7aa;
}
.priority-item--medium {
  background: #fffdf3;
  border-color: #fde68a;
}
.priority-item--medium .priority-item__indicator {
  background: #d97706;
}
.priority-item--medium .priority-item__count {
  color: #a16207;
  border: 1px solid #fde68a;
}
.priority-item--low {
  background: #f4fcf7;
  border-color: #bbf7d0;
}
.priority-item--low .priority-item__indicator {
  background: #16a34a;
}
.priority-item--low .priority-item__count {
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.priority-action {
  min-height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 17px;
  color: #ffffff !important;
  background: #173b63;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 900;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
}

.priority-action:hover {
  background: #0f2d4d;
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(23, 59, 99, 0.2);
}

.kpi-grid {
  display: grid;

  grid-template-columns: repeat(5, minmax(0, 1fr));

  gap: 16px;
  margin-bottom: 20px;
}

.kpi-card {
  min-width: 0;
  min-height: 165px;

  padding: 18px 16px;

  background: #ffffff;

  border: 1px solid #dde4ec;

  border-right: 5px solid #c3cedb;

  border-radius: 14px;

  box-shadow: 0 2px 7px rgba(15, 23, 42, 0.08);
}

.kpi-card.red {
  border-right-color: #b91c1c;
}

.kpi-card.orange {
  border-right-color: #b45309;
}

.kpi-card.blue {
  border-right-color: #2563eb;
}

.kpi-title {
  min-height: 48px;
  margin-bottom: 12px;

  color: #1a2433;

  font-size: 13px;
  font-weight: 900;
  line-height: 1.7;
}

.kpi-value {
  overflow-wrap: anywhere;

  color: #0f172a;

  text-align: right;
  font-size: 26px;
  font-weight: 900;
  line-height: 1.5;
}

.kpi-note {
  margin-top: 10px;

  color: #64748b;

  font-size: 12px;
  font-weight: 700;
  line-height: 1.7;
}

.axes-head {
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-bottom: 14px;
}

.axes-head h3 {
  margin: 0 0 5px;

  color: #0f172a;

  font-size: 18px;
  font-weight: 900;
}

.axes-head p {
  margin: 0;

  color: #64748b;

  font-size: 13px;
}

.axes-head span {
  padding: 6px 14px;

  color: #475569;

  font-size: 12px;
  font-weight: 800;

  background: #f8fafc;

  border: 1px solid #dde4ec;

  border-radius: 999px;
}

.summary-grid {
  display: grid;

  grid-template-columns: repeat(5, minmax(0, 1fr));

  gap: 14px;
}

.summary-card {
  padding: 16px;

  color: #071126;
  text-align: right;
  text-decoration: none;

  background: #ffffff;

  border: 1px solid #dbe3ee;

  border-top: 5px solid;

  border-radius: 16px;

  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);

  transition: 0.18s ease;
}

.summary-card:hover {
  transform: translateY(-3px);

  box-shadow: 0 16px 34px rgba(20, 58, 99, 0.14);
}

.summary-top {
  display: flex;
  align-items: center;
  gap: 8px;

  font-weight: 900;
}

.dot {
  display: inline-block;

  width: 12px;
  height: 12px;

  border-radius: 4px;
}

.summary-value {
  margin-top: 10px;

  color: #061226;

  font-size: 32px;
  font-weight: 900;
}

.summary-card p {
  margin: 0;

  color: #64748b;

  font-size: 12px;
  font-weight: 700;
}

.charts-grid {
  display: grid;
  gap: 20px;
}

.professional-charts {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.chart-card {
  min-width: 0;
  min-height: 510px;

  padding: 20px 22px;

  overflow: hidden;

  background: #ffffff;

  border: 1px solid #d7e0ea;

  border-radius: 14px;

  box-shadow: 0 2px 7px rgba(15, 23, 42, 0.08);
}

.chart-card h3 {
  margin-bottom: 18px;

  color: #061226;

  text-align: center;
  font-size: 17px;
  font-weight: 900;
}

.code-badge {
  display: inline-block;

  padding: 2px 10px;
  margin: 0 8px;

  color: #0f3f5f;

  font-size: 13px;
  font-weight: 900;

  background: #e0f2fe;

  border: 1px solid #bae6fd;

  border-radius: 6px;
}

.empty-chart {
  min-height: 350px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 20px;

  color: #64748b;
  text-align: center;
  font-weight: 700;

  background: #f8fafc;

  border: 1px dashed #cbd5e1;

  border-radius: 12px;
}

:deep(.v-field),
:deep(.v-label),
:deep(.v-field__input),
:deep(.v-select__selection-text),
:deep(.v-btn),
:deep(.apexcharts-text),
:deep(.apexcharts-legend-text),
:deep(.apexcharts-datalabel),
:deep(.apexcharts-tooltip) {
  font-family: "Cairo", sans-serif !important;
}

@media (max-width: 1200px) {
  .priority-panel {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .priority-action {
    grid-column: 1 / -1;
    width: 100%;
  }

  .priority-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 1400px) {
  .filter-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .kpi-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 1100px) {
  .filter-grid,
  .kpi-grid,
  .summary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .professional-charts {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .dashboard-page {
    width: 100%;
    padding: 12px 8px 32px;
    overflow-x: hidden;
  }

  .section-head {
    margin-bottom: 14px;
    text-align: start;
  }

  .section-head h2 {
    margin-bottom: 8px;
    font-size: 18px;
    line-height: 1.6;
  }

  .section-head p {
    font-size: 12px;
    line-height: 1.8;
  }

  .section-head span {
    width: 100%;
    padding: 7px 10px;
    border-radius: 10px;
    text-align: start;
    line-height: 1.7;
  }

  .filter-panel,
  .axes-panel {
    padding: 12px;
    margin-bottom: 14px;
    border-radius: 12px;
  }

  .filter-grid,
  .kpi-grid,
  .summary-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
  }

  .filter-grid :deep(.v-btn) {
    width: 100%;
    min-height: 44px;
  }

  .priority-panel {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 13px;
  }

  .priority-panel__icon {
    display: none;
  }

  .priority-panel__heading {
    align-items: flex-start;
  }

  .priority-panel__heading p {
    line-height: 1.7;
  }

  .priority-summary {
    grid-template-columns: 1fr;
  }

  .priority-item {
    min-height: 60px;
  }

  .priority-action {
    width: 100%;
  }

  .kpi-card {
    min-height: auto;
    padding: 14px;
    border-right-width: 4px;
    border-radius: 12px;
  }

  [dir="ltr"] .kpi-card {
    border-right-width: 1px;
    border-left: 4px solid #c3cedb;
  }

  [dir="ltr"] .kpi-card.red {
    border-left-color: #b91c1c;
  }

  [dir="ltr"] .kpi-card.orange {
    border-left-color: #b45309;
  }

  [dir="ltr"] .kpi-card.blue {
    border-left-color: #2563eb;
  }

  .kpi-title {
    min-height: auto;
    margin-bottom: 6px;
    font-size: 12px;
  }

  .kpi-value {
    font-size: 23px;
  }

  .kpi-note {
    margin-top: 5px;
    font-size: 11px;
  }

  .axes-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .axes-head h3 {
    font-size: 16px;
  }

  .axes-head p {
    font-size: 12px;
    line-height: 1.7;
  }

  .summary-card {
    padding: 14px;
    border-radius: 12px;
  }

  .summary-value {
    margin-top: 6px;
    font-size: 26px;
  }

  .professional-charts {
    grid-template-columns: minmax(0, 1fr);
    gap: 14px;
  }

  .chart-card {
    min-height: auto;
    padding: 14px 8px;
    overflow-x: auto;
    border-radius: 12px;
    -webkit-overflow-scrolling: touch;
  }

  .chart-card h3 {
    position: sticky;
    right: 0;
    left: 0;
    z-index: 2;
    margin-bottom: 10px;
    padding-inline: 6px;
    font-size: 14px;
    line-height: 1.7;
  }

  .chart-card :deep(.apexcharts-canvas) {
    min-width: 620px !important;
  }

  .chart-card :deep(.apexcharts-legend) {
    max-width: 620px;
    flex-wrap: wrap;
    justify-content: center !important;
  }

  .loading-card {
    width: calc(100% - 28px);
    padding: 26px 18px;
    border-radius: 16px;
  }

  :deep(.v-field__input) {
    min-height: 44px;
    font-size: 13px;
  }

  :deep(.v-label) {
    font-size: 13px;
  }
}

.dashboard-page[dir="ltr"] {
  text-align: left;
}

.dashboard-page[dir="ltr"] .alert-text,
.dashboard-page[dir="ltr"] .kpi-value,
.dashboard-page[dir="ltr"] .summary-card {
  text-align: left;
}
</style>
